/**
 * Máscaras e formatação para inputs de placeholders.
 * Número por extenso em português (0-999) para uso em contratos.
 */

export const INPUT_TYPES = [
  { value: 'text', label: 'Texto' },
  { value: 'number', label: 'Número' },
  { value: 'currency', label: 'Valor monetário' },
  { value: 'cpf', label: 'CPF' },
  { value: 'cnpj', label: 'CNPJ' },
  { value: 'email', label: 'E-mail' },
  { value: 'date', label: 'Data' },
  { value: 'phone', label: 'Telefone' },
] as const;

export type InputType = (typeof INPUT_TYPES)[number]['value'];

const UNIDADES = [
  'zero', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove',
  'dez', 'onze', 'doze', 'treze', 'catorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove',
];
const DEZENAS = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
const CENTENAS = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];

/** Converte número inteiro (0-999) para português por extenso. */
export function numeroPorExtenso(n: number): string {
  if (n < 0 || n > 999 || !Number.isInteger(n)) return String(n);
  if (n === 0) return UNIDADES[0];
  if (n < 20) return UNIDADES[n];
  if (n < 100) {
    const d = Math.floor(n / 10);
    const u = n % 10;
    return u === 0 ? DEZENAS[d] : `${DEZENAS[d]} e ${UNIDADES[u]}`;
  }
  const c = Math.floor(n / 100);
  const rest = n % 100;
  if (rest === 0) return c === 1 ? 'cem' : CENTENAS[c];
  const restStr = numeroPorExtenso(rest);
  return `${CENTENAS[c]} e ${restStr}`;
}

/** Converte número inteiro (0-999.999) para português por extenso (milhares). */
function numeroPorExtensoMilhares(n: number): string {
  if (n < 0 || n > 999_999 || !Number.isInteger(n)) return String(n);
  if (n < 1000) return numeroPorExtenso(n);
  const part = Math.floor(n / 1000);
  const rest = n % 1000;
  const partStr = part === 1 ? 'mil' : `${numeroPorExtenso(part)} mil`;
  if (rest === 0) return partStr;
  const restStr = numeroPorExtenso(rest);
  const lig = rest < 100 ? ' e ' : ' ';
  return partStr + lig + restStr;
}

/** Converte valor monetário (ex: "R$ 1.234,56") para por extenso: "um mil duzentos e trinta e quatro reais e cinquenta e seis centavos". */
export function valorMonetarioPorExtenso(currencyValue: string): string {
  const digits = (currencyValue || '').replace(/\D/g, '');
  if (digits.length === 0) return 'zero reais';
  const padded = digits.padStart(3, '0');
  const reais = parseInt(padded.slice(0, -2), 10);
  const centavos = parseInt(padded.slice(-2), 10);

  const reaisStr =
    reais === 0
      ? ''
      : reais === 1
        ? 'um real'
        : `${numeroPorExtensoMilhares(reais)} reais`;
  const centavosStr =
    centavos === 0
      ? ''
      : centavos === 1
        ? 'um centavo'
        : `${numeroPorExtenso(centavos)} centavos`;

  if (reaisStr && centavosStr) return `${reaisStr} e ${centavosStr}`;
  if (reaisStr) return reaisStr;
  if (centavosStr) return centavosStr;
  return 'zero reais';
}

/** Formata valor monetário com extenso: "R$ 1.234,56 (um mil duzentos e trinta e quatro reais e cinquenta e seis centavos)". */
export function formatCurrencyWithExtenso(currencyValue: string): string {
  const trimmed = (currencyValue || '').trim();
  if (!trimmed) return '';
  const ext = valorMonetarioPorExtenso(trimmed);
  return `${trimmed} (${ext})`;
}

/** Formata valor numérico com extenso e sufixo: "2 (dois) meses" ou "1 (um) mês". */
export function formatNumberWithSuffix(
  value: string,
  suffixSingular: string,
  suffixPlural: string,
): string {
  const n = parseInt(value.replace(/\D/g, '') || '0', 10);
  const ext = numeroPorExtenso(n);
  const suffix = n === 1 ? suffixSingular : suffixPlural;
  return `${n} (${ext}) ${suffix}`.trim();
}

/** Aplica máscara CPF: 000.000.000-00 */
export function maskCpf(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

/** Aplica máscara CNPJ: 00.000.000/0000-00 */
export function maskCnpj(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

/** Aplica máscara telefone: (00) 00000-0000 ou (00) 0000-0000 */
export function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3').replace(/-$/, '');
  }
  return digits.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3').replace(/-$/, '');
}

/** Aplica máscara data: dd/mm/aaaa */
export function maskDate(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{2})(\d)/, '$1/$2');
}

/** Aplica máscara valor monetário: R$ 1.234,56 (formato brasileiro) */
export function maskCurrency(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 15);
  if (digits.length === 0) return '';
  const padded = digits.padStart(3, '0');
  const intPart = padded.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const decPart = padded.slice(-2);
  return `R$ ${intPart},${decPart}`;
}

export function applyMask(type: string, value: string): string {
  switch (type) {
    case 'cpf':
      return maskCpf(value);
    case 'cnpj':
      return maskCnpj(value);
    case 'phone':
      return maskPhone(value);
    case 'date':
      return maskDate(value);
    case 'currency':
      return maskCurrency(value);
    default:
      return value;
  }
}

// --- Fórmulas (expressões que referenciam outros placeholders) ---

/** Extrai valor numérico de uma string (ex: "R$ 1.234,56" → 1234.56, "2 (dois) meses" → 2). */
export function getNumericValue(value: string): number {
  if (value == null || value === '') return 0;
  const s = String(value).trim();
  const normalized = s
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/\s+/g, '')
    .replace(/R\$\s?/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const num = parseFloat(normalized);
  if (!Number.isNaN(num)) return num;
  const digits = s.replace(/\D/g, '');
  return digits.length ? parseInt(digits, 10) : 0;
}

/** Retorna as chaves de placeholders que aparecem na fórmula (por nome exato). */
export function getFormulaDependencies(formula: string, possibleKeys: string[]): string[] {
  if (!formula?.trim() || !possibleKeys.length) return [];
  const upper = formula.toUpperCase();
  return possibleKeys.filter((key) => {
    const keyUpper = key.toUpperCase();
    const idx = upper.indexOf(keyUpper);
    if (idx === -1) return false;
    const before = upper[idx - 1];
    const after = upper[idx + keyUpper.length];
    const wordBoundaryBefore = idx === 0 || /[\s*+\-/(.]/.test(before ?? '');
    const wordBoundaryAfter = idx + keyUpper.length >= upper.length || /[\s*+\-/)]./.test(after ?? '');
    return wordBoundaryBefore && wordBoundaryAfter;
  });
}

const SAFE_EXPR_CHARS = /^[\d\s.+\-*/()]+$/;

/** Avalia uma expressão aritmética de forma segura. Fórmula pode conter nomes de placeholders; substitui por valores de `variables` e avalia. */
export function evaluateFormula(
  formula: string,
  variables: Record<string, string>,
  placeholderKeys: string[],
): number {
  if (!formula?.trim()) return 0;
  let expr = formula.trim();
  for (const key of placeholderKeys) {
    const val = getNumericValue(variables[key] ?? '');
    const re = new RegExp(`\\b${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
    expr = expr.replace(re, String(val));
  }
  expr = expr.replace(/\s+/g, '');
  if (!SAFE_EXPR_CHARS.test(expr)) return 0;
  try {
    const result = Function(`"use strict"; return (${expr})`)();
    return typeof result === 'number' && Number.isFinite(result) ? result : 0;
  } catch {
    return 0;
  }
}

/** Ordena chaves que têm fórmula para que dependências sejam calculadas antes (ordem topológica simples). */
function sortFormulaKeys(
  formulaKeys: string[],
  placeholderConfig: Record<string, { formula?: string }>,
  allKeys: string[],
): string[] {
  const deps = new Map<string, string[]>();
  for (const key of formulaKeys) {
    const formula = placeholderConfig[key]?.formula ?? '';
    deps.set(key, getFormulaDependencies(formula, allKeys));
  }
  const result: string[] = [];
  const added = new Set<string>();
  let changed = true;
  while (changed && result.length < formulaKeys.length) {
    changed = false;
    for (const key of formulaKeys) {
      if (added.has(key)) continue;
      const depList = deps.get(key) ?? [];
      const allDepComputed = depList.every((d) => allKeys.includes(d) && (added.has(d) || !formulaKeys.includes(d)));
      if (allDepComputed) {
        result.push(key);
        added.add(key);
        changed = true;
      }
    }
  }
  for (const key of formulaKeys) {
    if (!added.has(key)) result.push(key);
  }
  return result;
}

/** Mescla variáveis do usuário com valores computados por fórmula. Placeholders com fórmula ficam read-only no form. */
export function getVariablesWithComputed(
  variables: Record<string, string>,
  placeholderConfig: Record<string, { formula?: string }> | null | undefined,
  placeholderKeys: string[],
): Record<string, string> {
  if (!placeholderConfig || !placeholderKeys.length) return { ...variables };
  const formulaKeys = placeholderKeys.filter((k) => placeholderConfig[k]?.formula?.trim());
  if (formulaKeys.length === 0) return { ...variables };
  const merged = { ...variables };
  const ordered = sortFormulaKeys(formulaKeys, placeholderConfig, placeholderKeys);
  for (const key of ordered) {
    const formula = placeholderConfig[key]?.formula?.trim();
    if (!formula) continue;
    const value = evaluateFormula(formula, merged, placeholderKeys);
    merged[key] = String(value);
  }
  return merged;
}
